{
    This file is part of the Fresnel Library.
    Copyright (c) 2025 by the FPC & Lazarus teams.

    Pas2js Fresnel interface - Webassembly rendering API, simple main browser

    See the file COPYING.modifiedLGPL.txt, included in this distribution,
    for details about the copyright.

    This program is distributed in the hope that it will be useful,
    but WITHOUT ANY WARRANTY; without even the implied warranty of
    MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

 **********************************************************************}
{$mode objfpc}
{$h+}
{$modeswitch externalclass}
{$modeswitch advancedrecords}

{$DEFINE IMAGE_USEOSC}

// Fresnel API to use when everything is running in the main thread

unit Fresnel.Simple.Pas2js.WasmAPI;

interface

// Define this to disable API Logging altogether
{$DEFINE NOLOGAPICALLS}

// Define this to publish OffscreenCanvases to a window.fresnelOffscreen, main-thread only
{ $DEFINE PUBLISH_FRESNEL_OFFSCREEN}

// Define this to enable rendering statistics
{ $DEFINE ENABLE_RENDERING_STATISTICS}

uses
  Classes, SysUtils, Types,
  Web, JS, wasienv, WebAssembly,
  Fresnel.Keys,
  Fresnel.Wasm.Shared,
  Fresnel.Messages.Pas2js.WasmAPI,
  Fresnel.Shared.Pas2js,
  Fresnel.MenuBuilder.Pas2js.WasmAPI,
  Fresnel.Browser.Pas2js.WasmAPI;

type

  { TWasmFresnelSimpleApi }

  TWasmFresnelSimpleApi = class(TWasmFresnelBrowserApi)
  private
    {$ifdef ENABLE_RENDERING_STATISTICS}
    FStats_NbRAF : Integer;
    FStats_NbDOCOW : Integer;
    {$endif}

  protected
    procedure DoTimerTick; override;

  public
    constructor Create(aEnv : TPas2JSWASIEnvironment); override;

    procedure FillImportObject(aObject : TJSObject); override;

    // WindowCanvas

    // Allocate a window and, for main-thread rendering, an offscreen drawing
    // surface registered under the same window id (the wasm renderer draws
    // using the window id as its canvas id and presents with
    // draw_offscreen_on_window(win,win)).
    function AllocateWindowSurface(aSizeX, aSizeY: LongInt; aIDPtr: TWasmPointer; aPixelRatioPtr: PFresnelFloat; aPopup: Boolean): TCanvasError;

    function GetCanvasRect(const aID : TWindowCanvasID; aRectF: PFresnelFloat): TCanvasError;
    function SetCanvasRect(const aID : TWindowCanvasID; aLeft, aTop, aRight, aBottom: TFresnelFloat): TCanvasError;

    function GetCanvasSize(const aID : TWindowCanvasID; aPointF: PFresnelFloat): TCanvasError;
    function SetCanvasSize(const aID : TWindowCanvasID; aWidth, aHeight, aPixelRatio: TFresnelFloat): TCanvasError;

    procedure DrawOffscreenCanvasOnWindowCanvas(const aWindowID : TWindowCanvasID; const aCanvasID : TOffscreenCanvasID);

    function GetViewPortSizes(Flags : LongInt; aWidth, aHeight : PFresnelFloat) : TCanvasError;

    function GetSystemTheme : LongInt;

    // Menu

    function HandleMenuClick(aMenuID : TMenuID; aData : TWasmPointer) : Boolean; override;

    // RequestAnimationFrame

    procedure RequestAnimationFrame(userdata: TWasmPointer);

    // UserMedia

    function EnumerateUserMedia(aUserData : TWasmPointer) : TCanvasError;

  end;

// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------
implementation
// --------------------------------------------------------------------
// --------------------------------------------------------------------
// --------------------------------------------------------------------

// ---------------
// --------------- TWasmFresnelApi ---------------
// ---------------

// Create
//
constructor TWasmFresnelSimpleApi.Create(aEnv: TPas2JSWASIEnvironment);
begin
  inherited Create(aEnv);

  {$IFDEF PUBLISH_FRESNEL_OFFSCREEN}
  window['fresnelOffscreen'] := FOffscreenCanvases;
  {$ENDIF}

  {$ifdef ENABLE_RENDERING_STATISTICS}
  Window.setInterval(
    procedure
    begin
      Document.title := Format('Raf: %1.f   DoCoW: %.1f', [ FStats_NbRAF /2, FStats_NbDOCOW / 2 ]);
      FStats_NbRAF := 0;
      FStats_NbDOCOW := 0;
    end, 2000
  );
  {$endif}
end;

// FillImportObject
//
procedure TWasmFresnelSimpleApi.FillImportObject(aObject: TJSObject);
begin
  inherited FillImportObject(aObject);

  FImportObject := aObject;

  // Window

  aObject['canvas_allocate_window'] := @AllocateWindowSurface;
  aObject['canvas_deallocate_window'] := @DeAllocateWindowCanvas;

  aObject['window_show_hide'] := @ShowHideWindow;
  aObject['window_set_zorder'] := @SetWindowZOrder;

  aObject['canvas_draw_offscreen_on_window'] := @DrawOffscreenCanvasOnWindowCanvas;

  aObject['canvas_getrect'] := @GetCanvasRect;
  aObject['canvas_setrect'] := @SetCanvasRect;
  aObject['canvas_getsize'] := @GetCanvasSize;
  aObject['canvas_setsize'] := @SetCanvasSize;

  aObject['canvas_set_title'] := @SetWindowTitle;

  aObject['canvas_get_viewport_sizes'] := @GetViewPortSizes;

  aObject['system_theme_get'] := @GetSystemTheme;

  // Cursor

  aObject['cursor_set'] := @SetCursor;

  // Pointer capture

  aObject['pointer_capture_set'] := @SetPointerCapture;
  aObject['pointer_capture_release'] := @ReleasePointerCapture;

  // RequestAnimationFrame

  aObject['request_animation_frame'] := @RequestAnimationFrame;

  // Clipboard

  aObject['clipboard_read_text'] := @ClipboardReadText;
  aObject['clipboard_write_text'] := @ClipboardWriteText;

  // Event

  aObject['event_set_special_keymap'] := @SetSpecialKeyMap;
  aObject['wake_main_thread'] := @WakeMainThread;

  // Menu

  aObject['menu_add_item'] := @AddMenuItem;
  aObject['menu_remove_item'] := @DeleteMenuItem;
  aObject['menu_update_item'] := @UpdateMenuItem;

  // UserMedia & DisplayMedia

  aObject['usermedia_enumerate'] := @EnumerateUserMedia;
  aObject['usermedia_getenumerated'] := @GetEnumeratedUserMedia;
  aObject['displaymedia_getenumerated'] := @GetEnumeratedDisplayMedia;

  aObject['usermedia_startcapture'] := @UserMediaStartCapture;
  aObject['displaymedia_startcapture'] := @DisplayMediaStartCapture;

  aObject['media_stopcapture'] := @MediaStopCapture;
  aObject['media_iscapturing'] := @MediaIsCapturing;

end;

// DoTimerTick
//
var
  vLastTimeTimerTick : NativeInt;
procedure TWasmFresnelSimpleApi.DoTimerTick;
var
  Callback : JSValue;
  T : NativeInt;
begin
  T := vLastTimeTimerTick;
  vLastTimeTimerTick := TJSDate.now;
  if not assigned(InstanceExports) then
    Console.log('DoTimerTick: no instance exports !')
  else
  begin
    Callback := InstanceExports['__fresnel_tick'];
    if Assigned(Callback) then
      TTimerCallback(CallBack)(vLastTimeTimerTick, T)
    else
      Console.warn('DoTimerTick: no tick callback !');
  end;
end;

// GetCanvasRect
//
function TWasmFresnelSimpleApi.GetCanvasRect(const aID: TWindowCanvasID; aRectF: PFresnelFloat): TCanvasError;
var
  lWinRef: TWindowReference;
  v : TJSDataView;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.GetCanvasRect(%d,[%x])',[aID,aRectF]);
  {$ENDIF}
  lWinRef := GetWindowRef(aID);
  if not Assigned(lWinRef) then
    Exit(ECANVAS_NOCANVAS);
  v := MemoryDataView;
  v.setFloat32(aRectF                , lWinRef.Left,                 env.IsLittleEndian);
  v.setFloat32(aRectF +   SizeFloat32, lWinRef.Top,                  env.IsLittleEndian);
  v.setFloat32(aRectF + 2*SizeFloat32, lWinRef.Left + lWinRef.Width, env.IsLittleEndian);
  v.setFloat32(aRectF + 3*SizeFloat32, lWinRef.Top + lWinRef.Height, env.IsLittleEndian);
  Result := ECANVAS_SUCCESS;
end;

// SetCanvasRect
//
function TWasmFresnelSimpleApi.SetCanvasRect(const aID: TWindowCanvasID; aLeft, aTop, aRight, aBottom: TFresnelFloat): TCanvasError;
var
  Ref: TWindowReference;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.SetCanvasRect(%d,[%g,%g,%g,%g])',[aID,aLeft, aTop, aRight, aBottom]);
  {$ENDIF}
  Ref := GetWindowRef(aID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  Ref.SetPos(aLeft, aTop);
  Ref.SetSize(Round(aRight-aLeft), Round(aBottom-aTop));
end;

// GetCanvasSize
//
function TWasmFresnelSimpleApi.GetCanvasSize(const aID: TWindowCanvasID; aPointF: PFresnelFloat): TCanvasError;
var
  Ref: TWindowReference;
  v : TJSDataView;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.GetCanvasSizes(%d,[%x],[%x])',[aID,aWidth,aHeight]);
  {$ENDIF}
  Ref := GetWindowRef(aID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  v := MemoryDataView;
  v.setFloat32(aPointF,               Ref.Width,  env.IsLittleEndian);
  v.setFloat32(aPointF + SizeFloat32, Ref.Height, env.IsLittleEndian);
  Result := ECANVAS_SUCCESS;
end;

// SetCanvasSize
//
function TWasmFresnelSimpleApi.SetCanvasSize(const aID: TWindowCanvasID; aWidth, aHeight, aPixelRatio: TFresnelFloat): TCanvasError;
var
  Ref: TWindowReference;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.SetCanvasSizes(%d,%d,%d)', [aID,aWidth,aHeight]);
  {$ENDIF}
  Ref := GetWindowRef(aID);
  if not Assigned(Ref) then
    Exit(ECANVAS_NOCANVAS);
  Ref.SetSize(aWidth, aHeight);
  if aPixelRatio <> 0 then
    Ref.ChangePixelRatio(aPixelRatio);

  { The wasm draws on the offscreen canvas that AllocateWindowSurface registered
    under this same window id, and that drawing is then copied onto the window.
    So it has to grow along: while it stays small, everything drawn outside it is
    lost and the window keeps an empty edge. }
  ResizeOffscreenCanvas(aID, Round(Ref.Width), Round(Ref.Height), Ref.PixelRatio);

  Result := ECANVAS_SUCCESS;
end;

// DrawOffscreenCanvasOnWindowCanvas
//
procedure TWasmFresnelSimpleApi.DrawOffscreenCanvasOnWindowCanvas(const aWindowID: TWindowCanvasID; const aCanvasID: TOffscreenCanvasID);
var
  srcCanvas : TJSHTMLOffscreenCanvasElement;
  destWindow : TWindowReference;
begin
  srcCanvas := GetOffscreenCanvasRef(aCanvasID).Canvas;

  destWindow := GetWindowRef(aWindowID);
  destWindow.CanvasContext.drawImage(srcCanvas, 0, 0);

  {$ifdef ENABLE_RENDERING_STATISTICS}
  Inc(FStats_NbDOCOW);
  {$endif}
end;

// AllocateWindowSurface
//
function TWasmFresnelSimpleApi.AllocateWindowSurface(aSizeX, aSizeY: LongInt;
  aIDPtr: TWasmPointer; aPixelRatioPtr: PFresnelFloat; aPopup: Boolean): TCanvasError;
var
  lWinID : TWindowCanvasID;
  lPixelRatio : TFresnelFloat;
begin
  Result := inherited AllocateWindowCanvas(aSizeX, aSizeY, aIDPtr, aPixelRatioPtr, aPopup);
  if Result <> ECANVAS_SUCCESS then
    Exit;
  { When there is no worker, the wasm renders directly onto the window using
    the window id as its canvas id. Register an offscreen canvas under that
    same id so the drawing calls have a target; draw_offscreen_on_window then
    blits it onto the visible window canvas. }
  lWinID := MemoryDataView.getUint32(aIDPtr, env.IsLittleEndian);
  lPixelRatio := MemoryDataView.getFloat32(aPixelRatioPtr, env.IsLittleEndian);
  CreateOffscreenCanvas(lWinID, aSizeX, aSizeY, lPixelRatio);
end;

// GetViewPortSizes
//
function TWasmFresnelSimpleApi.GetViewPortSizes(Flags: LongInt; aWidth, aHeight: PFresnelFloat): TCanvasError;
var
  W,H : NativeInt;
  v : TJSDataView;
  s : TSizeF;
begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('Canvas.GetViewPortSizes(%d,[%x],[%x])',[Flags,aWidth,aHeight]);
  {$ENDIF}
  if Flags=GETVIEWPORTSIZE_CLIENT then
  begin
    s := GetWindowsParentRootSize;
    W := Round(s.cx);
    H := Round(s.cy);
  end
  else if FLAGS=GETVIEWPORTSIZE_WINDOW then
  begin
    W:=Window.innerWidth;
    H:=document.documentElement.clientHeight;
  end
  else
  begin
    W:=0;
    H:=0;
  end;
  if (W=0) or (H=0) then
    Result:=ECANVAS_INVALIDPARAM
  else
  begin
    V:=MemoryDataView;
    v.setFloat32(aWidth,  W, env.IsLittleEndian);
    v.setFloat32(aHeight, H, env.IsLittleEndian);
    Result:=ECANVAS_SUCCESS;
  end;
end;

// GetSystemTheme
//
function TWasmFresnelSimpleApi.GetSystemTheme: LongInt;
begin
  // 0 = unspecified, 1 = light, 2 = dark
  Result := 0;
  if Window.matchMedia('(prefers-color-scheme: dark)').matches then
    Result := 2
  else if Window.matchMedia('(prefers-color-scheme: light)').matches then
    Result := 1;
end;

// HandleMenuClick
//
function TWasmFresnelSimpleApi.HandleMenuClick(aMenuID: TMenuID; aData: TWasmPointer): Boolean;
var
  Callback : JSValue;
begin
  Result:=False;
  if not assigned(InstanceExports) then
    Console.warn('No instance exports !')
  else
  begin
    Callback:=InstanceExports['__fresnel_menu_click'];
    if Assigned(Callback) then
    begin
      TMenuClickCallback(CallBack)(aMenuID,AData);
      Result:=True;
    end
    else
      Console.warn('No menu click callback !');
  end;
end;

// RequestAnimationFrame
//
procedure TWasmFresnelSimpleApi.RequestAnimationFrame(userdata: TWasmPointer);

  procedure HandleAnimationFrame(timestamp{%H-} : Double);
  var
    lCallbackValue : JSValue;
    lCallback : TAnimationFrameCallback absolute lCallbackValue;
  begin
    lCallbackValue := InstanceExports['__fresnel_animation_frame'];
    if lCallbackValue then
      lCallback();
  end;

begin
  {$IFNDEF NOLOGAPICALLS}
  if LogAPICalls then
    LogCall('FresnelApi.RequestAnimationFrame');
  {$ENDIF}

  window.requestAnimationFrame(@HandleAnimationFrame);

  {$ifdef ENABLE_RENDERING_STATISTICS}
  Inc(FStats_NbRAF);
  {$endif}
end;

// EnumerateUserMedia
//
function TWasmFresnelSimpleApi.EnumerateUserMedia(aUserData: TWasmPointer): TCanvasError;
begin
  FEnumeratedUserMedia := '';
  DoEnumerateUserMedia(
    procedure
    var
      callback : JSValue;
    begin
      callback := InstanceExports['__fresnel_usermedia_enumerated'];
      if callback then
        TUserMediaCallback(callback)(Length(FEnumeratedUserMedia), aUserData);
    end
  );
  Result := ECANVAS_SUCCESS;
end;

end.
